import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FleetPage, FleetSection, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { FleetGrid, useGridList } from '../../../../shared/sso-workspace/fleet-grid'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, formatDateTime } from '../../../../shared/sso-workspace/format'
import { TableAgClient } from '../../../../shared/table-builder/table-ag-client'
import { ColumnFormat, ColumnMenu, ColumnText } from '../../../../shared/table-builder/table-columns'
import { Menu, MenuItem } from '../../../../shared/menu/menu'
import { IconAdd, IconEdit, IconOrganization } from '../../../../shared/icon/icon'
import { isCode, serverMessage } from '../../../../shared/error/server-message'
import { contentGet } from '../../../../shared/email-activity/content-api'
import { getRetention, putRetention } from '../../../../shared/audit/audit-api'
import { OverrideDrawer } from './override-drawer'
import { lookupOrganizations } from '../../../../shared/org-search/org-search-api'
import { NOT_AVAILABLE, ORG_PERIODS, boundsLine, boundsProblem, days, listOf, months, orgName, rangeOf, retentionError } from './retention-format'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import './retention.scss'

// Every platform period is a setting (phase 5 Task 8). help: the line under
// the field. optional: shown only when the server returns it (a field added
// later than the page, until every server has it).
const PLATFORM_FIELDS = [
  { key: 'changesMonths', label: 'Keep change history for (months)', help: 'Also applies to named events (sign-ins, exports and the like).' },
  { key: 'requestDays', label: 'Keep request log for (days)' },
  { key: 'triggerErrorsDays', label: 'Keep audit write errors for (days)', help: 'Errors recorded while the audit trail itself was being written.' },
  { key: 'viewsDays', label: 'Keep record views for (days)' },
  { key: 'launchesDays', label: 'Keep course launches for (days)' },
  {
    key: 'exportFilesDays',
    label: 'Keep export files for (days)',
    help: "Applies to everyone's exports; no per-organization override. A change applies only to exports made after you save; files already made keep the period they were made with."
  },
  { key: 'staffAlertDays', label: 'Keep staff alerts for (days)', help: 'Alert contents and delivery history. The small keys that stop an alert being sent twice are kept for good.', optional: true },
  // The notification platform (C-3 audit.settings notify_*_days).
  { key: 'notifyInboxDays', label: 'Keep notifications in inboxes for (days)', help: "What each person sees in their notifications, read or not. Every organization's.", optional: true },
  { key: 'notifyDeliveryDays', label: 'Keep notification delivery history for (days)', help: 'Email and push deliveries, their attempts and the captured payloads.', optional: true },
  { key: 'notifyEventDays', label: 'Keep relayed notification events for (days)', help: 'What the relay moved out of the outbox. Short: the deliveries keep the history.', optional: true }
]

const hasField = (f, platform) => !f.optional || Boolean(platform && platform[f.key] !== undefined && platform[f.key] !== null)

const PURPOSE = 'How long the audit trail keeps entries, per organization, and which organizations are on legal hold. Every change here is recorded.'
const PAUSED = 'Deleting old entries is paused on this server. Nothing is deleted until it is resumed.'
const READ_ONLY = "You're signed in as someone else, so retention settings can be viewed but not changed. End the impersonation to make changes."
const NAMES_FAILED = "Organization names couldn't be loaded, so organizations show by number. To add an override, enter the organization's ID."
const LOAD_FAILED = "Retention settings couldn't be loaded."

const toForm = platform => {
  const v = {}
  PLATFORM_FIELDS.forEach(f => {
    v[f.key] = platform && platform[f.key] !== undefined && platform[f.key] !== null ? String(platform[f.key]) : ''
  })
  return v
}

// The Audit retention page (super admins with caps.retention; the route
// gates it): the platform defaults, the per-organization overrides in the
// shared grid, and the drawer that edits one. readOnly (an impersonated
// session: content refuses every retention write from one) keeps every
// write disabled and says why.
export const RetentionPage = ({ readOnly = false }) => {
  const [view, setView] = useState(undefined) // undefined loading, null failed
  const [loadError, loadFailure, setLoadError] = useErrorLine()
  const [reload, setReload] = useState(0)
  const [orgs, setOrgs] = useState([])
  const [orgsFailed, setOrgsFailed] = useState(false)
  const [switchedOff, setSwitchedOff] = useState([])
  const [pageNotice, pageFailure, setPageNotice] = useErrorLine()
  const [form, setForm] = useState(toForm(null))
  const [saving, setSaving] = useState(false)
  const [saveError, saveFailure, setSaveError] = useErrorLine()
  const [saved, setSaved] = useState(false)
  const [drawer, setDrawer] = useState(null) // { override } | null
  const tableRef = useRef()
  const live = useRef(true)

  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  // Organization names: the switched-off ones, read once (they may still
  // have overrides; best effort: without them those rows show by number).
  useEffect(() => {
    contentGet('organization/deleted').then(
      v => live.current && setSwitchedOff(listOf(v)),
      () => {}
    )
  }, [])

  useEffect(() => {
    getRetention().then(
      v => {
        if (!live.current) return
        setLoadError(null)
        setView(v)
        setForm(toForm(v && v.platform))
      },
      err => {
        if (!live.current) return
        setView(null)
        setLoadError(isCode(err, 'retention.not_available') ? NOT_AVAILABLE : serverMessage(err, LOAD_FAILED), err)
      }
    )
  }, [reload, setLoadError])

  // The live ones: only the organizations with an override, looked up by id
  // (owner 2026-10-13: never the whole list). It needs the separate
  // organization permission, so a failure is said, and an override can then
  // be added by ID.
  const overrideIDs = useMemo(() => (view && view.organizations ? view.organizations.map(o => o.organizationID).sort((a, b) => a - b) : []), [view])
  const overrideKey = overrideIDs.join(',')
  useEffect(() => {
    if (!view) return
    lookupOrganizations(overrideIDs).then(
      found => {
        if (!live.current) return
        setOrgs([...found.values()])
        setOrgsFailed(false)
      },
      () => live.current && setOrgsFailed(true)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideKey, Boolean(view)])

  const platform = view && view.platform
  const bounds = view && view.bounds
  const names = useMemo(() => {
    const m = new Map(switchedOff.filter(o => o && o.name).map(o => [Number(o.id), `${o.name} (switched off)`]))
    orgs.forEach(o => m.set(Number(o.id), o.name))
    return m
  }, [orgs, switchedOff])

  const stored = useMemo(() => toForm(platform), [platform])
  const fields = PLATFORM_FIELDS.filter(f => hasField(f, platform))
  const anyBlank = fields.some(f => form[f.key].trim() === '')
  // Only the fields that changed: content merges the body over the stored
  // row, so a tab loaded earlier never puts back what someone else changed.
  const changedKeys = fields.filter(f => form[f.key] !== stored[f.key]).map(f => f.key)
  const dirty = changedKeys.length > 0
  const problems = {}
  fields.forEach(f => {
    const problem = boundsProblem(f.key, form[f.key], bounds)
    if (problem) problems[f.key] = problem
  })
  const invalid = Object.keys(problems).length > 0
  const canSaveDefaults = !readOnly && !saving && dirty && !anyBlank && !invalid

  const saveDefaults = async e => {
    e.preventDefault()
    if (!canSaveDefaults) return
    const body = {}
    changedKeys.forEach(k => {
      body[k] = Number(form[k])
    })
    setSaving(true)
    setSaveError(null)
    setSaved(false)
    try {
      const next = await putRetention(body)
      if (!live.current) return
      const settled = next && typeof next === 'object' ? { ...platform, ...next } : { ...platform, ...body }
      setView(v => ({ ...v, platform: settled }))
      setForm(toForm(settled))
      setSaved(true)
    } catch (err) {
      if (live.current) setSaveError(retentionError(err, { bounds, sent: body, fallback: "The defaults couldn't be saved. Please try again." }).message, err)
    } finally {
      if (live.current) setSaving(false)
    }
  }

  // The grid's rows: the override plus its organization's name (sorting and
  // the quick filter read it) and the platform defaults its blanks follow.
  const rows = useMemo(() => {
    if (view === undefined) return undefined
    if (view === null) return null
    return (view.organizations || []).map(o => ({ ...o, id: o.organizationID, name: orgName(names, o.organizationID), platform: view.platform || {} }))
  }, [view, names])
  const list = useGridList(rows)

  const openRef = useRef(setDrawer)
  openRef.current = setDrawer
  const rowMenu = useMemo(
    () => r =>
      <Menu small>
        <MenuItem Icon={IconEdit} text="edit" pin show onClick={() => { setPageNotice(null); openRef.current({ override: r }) }} />
      </Menu>,
    [setPageNotice]
  )
  const headerMenu = useMemo(
    () => [<MenuItem key="add" Icon={IconAdd} text="Add override" pin show disabled={readOnly} onClick={() => { setPageNotice(null); openRef.current({ override: null }) }} />],
    [readOnly, setPageNotice]
  )

  const onSaved = useCallback(() => {
    setDrawer(null)
    setReload(n => n + 1)
  }, [])

  // A refusal caused by a change made elsewhere: close, reload, say so.
  const onStale = useCallback((message, err) => {
    setDrawer(null)
    setPageNotice(message, err)
    setReload(n => n + 1)
  }, [setPageNotice])

  const taken = useMemo(() => (view && view.organizations ? view.organizations.map(o => Number(o.organizationID)) : []), [view])

  return (
    <FleetPage title="Audit retention" purpose={PURPOSE} testId="audit-retention">
      {readOnly && <Notice tone="warn" role="status">{READ_ONLY}</Notice>}
      {bounds && bounds.paused && <Notice tone="warn" role="status">{PAUSED}</Notice>}
      {orgsFailed && view && <Notice role="status">{NAMES_FAILED}</Notice>}
      {pageNotice && <Notice tone="warn" role="alert">{pageNotice}<ErrorReference error={pageFailure} /></Notice>}
      {loadError && <Notice tone={loadError === NOT_AVAILABLE ? 'warn' : 'bad'} role="alert">{loadError}<ErrorReference error={loadFailure} /></Notice>}

      {view &&
        <FleetSection id="rt-platform" title="Platform defaults" lede="Every organization follows these unless it has its own override below.">
          <form onSubmit={saveDefaults}>
            <div className="rt-periods">
              {fields.map(f =>
                <div className="sw-field" key={f.key}>
                  <label className="sw-label" htmlFor={`rt-${f.key}`}>{f.label}</label>
                  <input
                    id={`rt-${f.key}`}
                    type="number"
                    inputMode="numeric"
                    min={bounds ? rangeOf(f.key, bounds).min : 1}
                    max={bounds ? rangeOf(f.key, bounds).max : undefined}
                    step={1}
                    className={`form-control${problems[f.key] ? ' is-invalid' : ''}`}
                    aria-invalid={problems[f.key] ? true : undefined}
                    aria-describedby={f.help ? `rt-${f.key}-help` : undefined}
                    value={form[f.key]}
                    onChange={e => {
                      const value = e.target.value
                      setSaved(false)
                      setForm(v => ({ ...v, [f.key]: value }))
                    }}
                  />
                  {problems[f.key] && <span className="sw-required" role="alert">{problems[f.key]}</span>}
                  {f.help && <span className="sw-hint" id={`rt-${f.key}-help`}>{f.help}</span>}
                </div>)}
            </div>
            {bounds && <p className="sw-hint mt-3 mb-2">{boundsLine(bounds)}</p>}
            {saveError && <p className="text-danger small" role="alert">{saveError}</p>}
            {saveError && <ErrorReference error={saveFailure} className="mb-2" />}
            {saved && <p className="text-success small" role="status">Defaults saved.</p>}
            <button type="submit" className="btn sw-btn-primary" disabled={!canSaveDefaults}>Save defaults</button>
          </form>
        </FleetSection>}

      {view &&
        <FleetSection id="rt-orgs" title="Organizations" lede="Organizations with their own periods or a legal hold. Anything not set follows the platform default.">
          {/* flush: the section is the grid's one container; Add override, export
              and table settings sit in the grid's toolbar row. */}
          <FleetGrid title="Overrides" tableRef={tableRef} exportName="audit-retention-overrides" menu={headerMenu} compact flush>
            <TableAgClient
              pinFirstColumn
              ref={tableRef}
              queryKey="retention"
              apiList={list}
              sortCol="name"
              emptyState={{ Icon: IconOrganization, title: 'No overrides', message: 'Every organization follows the platform defaults.' }}>
              <ColumnText field="name" header="Organization" sortable />
              <ColumnFormat field="changesMonths" header="Change history" render={periodCell('changesMonths')} exportValue={periodText('changesMonths')} sortable />
              <ColumnFormat field="requestDays" header="Request log" render={periodCell('requestDays')} exportValue={periodText('requestDays')} sortable />
              <ColumnFormat field="viewsDays" header="Record views" render={periodCell('viewsDays')} exportValue={periodText('viewsDays')} sortable />
              <ColumnFormat field="launchesDays" header="Course launches" render={periodCell('launchesDays')} exportValue={periodText('launchesDays')} sortable />
              <ColumnFormat field="legalHold" header="Hold" render={holdCell} exportValue={r => (r.legalHold ? 'Legal hold' : '')} sortable />
              <ColumnText field="reason" header="Reason" sortable />
              <ColumnFormat field="updatedAt" header="Updated" render={updatedCell} exportValue={r => (r.updatedAt ? formatDateTime(r.updatedAt) : '')} sortable />
              <ColumnMenu menu={rowMenu} />
            </TableAgClient>
          </FleetGrid>
        </FleetSection>}

      {view &&
        <OverrideDrawer
          open={Boolean(drawer)}
          override={drawer ? drawer.override : null}
          organizations={orgs}
          taken={taken}
          orgsFailed={orgsFailed}
          platform={platform}
          bounds={bounds}
          readOnly={readOnly}
          onClose={() => setDrawer(null)}
          onSaved={onSaved}
          onStale={onStale}
        />}
    </FleetPage>
  )
}

const UNIT = Object.fromEntries(ORG_PERIODS.map(p => [p.key, p.key === 'changesMonths' ? months : days]))

// An override's period, or the platform default it follows.
const periodText = key => data => {
  const unit = UNIT[key]
  const own = data[key]
  if (own !== null && own !== undefined) return unit(own)
  const def = data.platform && data.platform[key]
  return def !== undefined && def !== null ? `Default (${unit(def)})` : 'Default'
}

const periodCell = key => row => {
  if (!row.data) return ''
  const own = row.data[key] !== null && row.data[key] !== undefined
  return <span className={own ? undefined : 'sw-hint'}>{periodText(key)(row.data)}</span>
}

const holdCell = row => (row.data && row.value ? <Pill tone="warn">Legal hold</Pill> : '')

const updatedCell = row => (row.data && row.value ? <span title={formatDateTime(row.value)}>{formatAgo(row.value)}</span> : '')
